import React, { useEffect, useRef, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { getProfile } from '../api'
import { isPlaceholderName } from '../lib/memberName'

type NameGate = { path: string; status: 'ok' | 'needs_name' }

// Everyone must have a real name before using the app. Fails OPEN: if the profile
// call errors we let the user through, and only redirect on a definite "no name".
export default function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth()
  const location = useLocation()
  const userId = session?.user?.id
  const email = session?.user?.email
  const nameConfirmed = useRef<string | null>(null)
  const [gate, setGate] = useState<NameGate | null>(null)

  useEffect(() => {
    if (!userId || nameConfirmed.current === userId) return
    let cancelled = false
    const path = location.pathname
    // A hung request must not lock the user out: fail open after 6s.
    const timer = setTimeout(() => { if (!cancelled) setGate({ path, status: 'ok' }) }, 6000)
    getProfile()
      .then(r => {
        clearTimeout(timer)
        if (cancelled) return
        if (isPlaceholderName(r.data?.display_name, email)) {
          setGate({ path, status: 'needs_name' })
        } else {
          nameConfirmed.current = userId
          setGate({ path, status: 'ok' })
        }
      })
      .catch(() => { clearTimeout(timer); if (!cancelled) setGate({ path, status: 'ok' }) })
    return () => { cancelled = true; clearTimeout(timer) }
  }, [userId, email, location.pathname])

  if (loading) return null
  if (!session) return <Navigate to="/login" replace />

  if (nameConfirmed.current !== userId) {
    // Re-check on every navigation until a name is saved. Hold rendering until
    // the check for this path has answered, so the app never flashes unnamed.
    if (!gate || gate.path !== location.pathname) return null
    if (gate.status === 'needs_name' && !location.pathname.startsWith('/onboarding')) {
      return <Navigate to="/onboarding" replace />
    }
  }
  return <>{children}</>
}
